import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import {
  getCompanyIntegrationsStatus,
  getDevelopmentsFilters,
  getDevelopmentsList,
} from './service';
import {
  ORIGIN_VALUES,
  PARAMS_QUERY_KEYS,
  getStatusFilterOptions,
  UNIVEN_INTEGRATIONS,
} from '@/components/Developments/constants';
import { completeFilterNames } from '@/components/Developments/utils';

const { TYPE, LOCATION } = PARAMS_QUERY_KEYS;

export const DevelopmentsSlice = createSlice({
  name: 'developments',
  initialState,
  reducers: {
    setInitialized(state, action) {
      state.isInitialized = action.payload;
    },

    setInitialFilters(state, action) {
      const filters = action.payload;
      const type = filters.get(TYPE);
      const location = filters.get(LOCATION);

      // Status params (commercialization, data_loading, archived) come as
      // `<param>=true` and can be combined.
      const status = Object.entries(getStatusFilterOptions())
        .filter(([param]) => filters.get(param) === 'true')
        .map(([, option]) => option);

      if (status.length) state.filtersSelections.status = status;

      if (type)
        state.filtersSelections.types = {
          id: Number(type),
        };

      if (location)
        state.filtersSelections.locations = {
          id: Number(location),
        };

      state.isInitialized = true;
    },

    setFiltersSelections(state, action) {
      const { key, value } = action.payload;

      state.filtersSelections[key] = value;
    },

    setFiltersReset(state, action) {
      const { key } = action.payload;

      state.filtersSelections[key] = initialState.filtersSelections[key];
    },

    setSearcherText(state, action) {
      state.searcherText = action.payload;
    },

    setFiltersMultipleSelections(state, action) {
      const { key, value } = action.payload;
      const isAlreadySelected = state.filtersSelections[key].some(
        (item) => item.id === value.id
      );

      if (isAlreadySelected) {
        state.filtersSelections[key] = state.filtersSelections[key].filter(
          (item) => item.id !== value.id
        );
        return;
      }

      state.filtersSelections[key] = [...state.filtersSelections[key], value];
    },

    setDeliveryDateSelection(state, action) {
      const { key, value } = action.payload;

      state.filtersSelections.deliveryDate[key] = value;
    },

    setPaginatorPage(state, action) {
      state.paginator.page = action.payload;
    },

    setListingEditionReviewSelection(state, action) {
      state.listingEditionReviewSelection = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDevelopmentsFilters.pending, () => {})
      .addCase(getDevelopmentsFilters.fulfilled, (state, action) => {
        const filters = action.payload;

        const {
          status,
          development_types,
          construction_status,
          development_locations,
          years,
          control_listing_edition_counts,
        } = filters;

        state.filtersData = {
          types: development_types,
          locations: development_locations,
          constructionsStatus: construction_status,
          status,
          deliveryDate: years,
        };

        state.controlListingEditionCounts =
          control_listing_edition_counts ?? null;

        // Complete missing names for initial filters from query params
        const updatedSelections = completeFilterNames(state.filtersSelections, {
          types: development_types,
          locations: development_locations,
        });
        state.filtersSelections = updatedSelections;
      })
      .addCase(getDevelopmentsFilters.rejected, () => {});
    builder
      .addCase(getDevelopmentsList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDevelopmentsList.fulfilled, (state, action) => {
        const { results, page_info, listing_edition_review_counts } =
          action.payload;

        const { has_next_page, num_pages, per_page, total, page, previous } =
          page_info;

        state.developments = results || [];

        state.paginator = {
          hasNext: has_next_page,
          numPages: num_pages,
          perPage: per_page,
          total,
          page,
          previous,
        };

        if (
          listing_edition_review_counts != null &&
          typeof listing_edition_review_counts === 'object'
        ) {
          state.listingEditionReviewCounts = listing_edition_review_counts;
          if (!Array.isArray(state.listingEditionReviewSelection)) {
            state.listingEditionReviewSelection = [];
          }
        } else {
          state.listingEditionReviewCounts = null;
          state.listingEditionReviewSelection = [];
        }

        state.loading = false;
      })
      .addCase(getDevelopmentsList.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(getCompanyIntegrationsStatus.fulfilled, (state, action) => {
      const integrations = action.payload;

      state.companyActiveIntegrations = Object.keys(integrations).filter(
        (key) =>
          UNIVEN_INTEGRATIONS.includes(key.toLowerCase()) &&
          integrations[key].is_active
      );

      const { ORULO, DWV } = integrations;
      const activesIntegrations = [];

      if (ORULO?.is_active)
        activesIntegrations.push({ id: ORIGIN_VALUES.ORULO });

      if (DWV?.is_active) activesIntegrations.push({ id: ORIGIN_VALUES.DWV });

      state.filtersSelections.origin = [
        ...state.filtersSelections.origin,
        ...activesIntegrations,
      ];
    });
  },
});

export const {
  setFiltersSelections,
  setFiltersMultipleSelections,
  setDeliveryDateSelection,
  setSearcherText,
  setPaginatorPage,
  setInitialFilters,
  setInitialized,
  setFiltersReset,
  setListingEditionReviewSelection,
} = DevelopmentsSlice.actions;

export default DevelopmentsSlice.reducer;
